import * as React from 'react';
import { TimeLine } from '@mingcute/react';

import { cn } from '../lib/cn';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './dialog';
import { Button } from './button';

export interface SessionWarningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seconds remaining until session expires. */
  remainingSeconds: number;
  onExtend: () => void;
  onSignOut: () => void;
  title?: string;
  description?: string;
  extendLabel?: string;
  signOutLabel?: string;
}

function formatRemaining(s: number): string {
  if (s <= 0) return '0:00';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export function SessionWarningModal({
  open,
  onOpenChange,
  remainingSeconds,
  onExtend,
  onSignOut,
  title = 'Your session is expiring soon',
  description,
  extendLabel = 'Extend session',
  signOutLabel = 'Sign out',
}: SessionWarningModalProps) {
  const [tick, setTick] = React.useState(remainingSeconds);

  React.useEffect(() => {
    setTick(remainingSeconds);
  }, [remainingSeconds]);

  React.useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setTick((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <TimeLine size={20} className="text-[var(--color-warning)]" aria-hidden />
            <DialogTitle>{title}</DialogTitle>
          </div>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="flex items-center justify-center py-2">
          <div
            className={cn(
              'font-mono text-3xl font-semibold tabular-nums',
              tick <= 30
                ? 'text-[var(--color-danger)]'
                : 'text-[var(--color-fg)]',
            )}
            aria-live="polite"
          >
            {formatRemaining(tick)}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onSignOut}>
            {signOutLabel}
          </Button>
          <Button variant="primary" onClick={onExtend}>
            {extendLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
